import { Link, useNavigate } from 'react-router-dom';
import useSWR from 'swr';
import axiosInstance from '@/lib/axios';
import { Button } from '@/components/ui/button';
import { GearSixIcon } from '@phosphor-icons/react';
import {
  type EventNavContext,
  eventScannerQuickAction,
  getEventManagementOptions,
  getEventScannerPath,
} from '@/lib/event-management-nav';

interface EventAdminActionsProps extends EventNavContext {
  variant: 'banner' | 'card';
}

type QuickAction = {
  key: string;
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  href: string;
  tone: string;
};

function useEventScannerPath(ctx: EventNavContext) {
  const { data: activities } = useSWR<{ id: string }[]>(
    ctx.eventId ? `/api/events/${ctx.eventId}/activities` : null,
    (url: string) => axiosInstance.get(url).then((res) => res.data)
  );

  return getEventScannerPath(ctx, activities);
}

function useQuickActions(ctx: EventNavContext, scannerPath: string): QuickAction[] {
  const options = getEventManagementOptions();
  const optionFor = (key: string) => options.find((option) => option.key === key);
  const registrations = optionFor('registrations');
  const edit = optionFor('edit');
  const admins = optionFor('admins');
  const marketing = optionFor('marketing');

  return [
    {
      key: 'scanner',
      title: eventScannerQuickAction.shortLabel,
      icon: eventScannerQuickAction.icon,
      href: scannerPath,
      tone: 'text-green-600',
    },
    {
      key: 'registrations',
      title: 'Attendees',
      icon: registrations?.icon ?? GearSixIcon,
      href:
        registrations?.getHref(ctx) ?? `/${ctx.communityId}/events/${ctx.eventId}/registrations`,
      tone: 'text-blue-600',
    },
    {
      key: 'edit',
      title: 'Edit event',
      icon: edit?.icon ?? GearSixIcon,
      href: edit?.getHref(ctx) ?? `/${ctx.communityId}/events/${ctx.eventId}/edit`,
      tone: 'text-orange-600',
    },
    {
      key: 'admins',
      title: 'Admins',
      icon: admins?.icon ?? GearSixIcon,
      href: admins?.getHref(ctx) ?? `/${ctx.communityId}/events/${ctx.eventId}/admins`,
      tone: 'text-primary',
    },
    {
      key: 'marketing',
      title: 'Marketing',
      icon: marketing?.icon ?? GearSixIcon,
      href: marketing?.getHref(ctx) ?? `/${ctx.communityId}/events/${ctx.eventId}/marketing`,
      tone: 'text-rose-600',
    },
  ];
}

export function EventAdminActions({ communityId, eventId, variant }: EventAdminActionsProps) {
  const navigate = useNavigate();
  const ctx = { communityId, eventId };
  const scannerPath = useEventScannerPath(ctx);
  const actions = useQuickActions(ctx, scannerPath);
  const hubPath = `/${communityId}/events/${eventId}/manage`;

  if (variant === 'card') {
    const bannerActions = actions.filter((action) =>
      ['scanner', 'registrations', 'edit'].includes(action.key)
    );
    return (
      <div
        className="absolute top-2 right-2 z-10 flex gap-2 md:opacity-0 md:group-hover:opacity-100 transition-opacity"
        onClick={(event) => event.stopPropagation()}
      >
        {bannerActions.map((action) => (
          <Button
            key={action.key}
            variant="secondary"
            size="icon"
            className="h-8 w-8 bg-background/90 backdrop-blur-sm hover:bg-background shadow-sm"
            title={action.title}
            onClick={(event) => {
              event.stopPropagation();
              navigate(action.href);
            }}
          >
            <action.icon className="h-4 w-4" />
          </Button>
        ))}
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-border/70 bg-card p-4 shadow-sm">
      <div className="mb-3 flex items-center justify-between gap-2">
        <p className="text-sm font-semibold text-foreground">Organiser tools</p>
        <Link to={hubPath} className="text-xs font-medium text-primary hover:underline">
          All tools
        </Link>
      </div>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5">
        {actions.map((action) => (
          <button
            key={action.key}
            type="button"
            onClick={() => navigate(action.href)}
            className="flex flex-col items-start gap-2 rounded-xl p-2 text-left transition hover:bg-primary/5"
          >
            <action.icon className={`h-6 w-6 ${action.tone}`} />
            <span className="text-sm font-medium text-foreground">{action.title}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
